import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import SearchBox from '@replicon/react-search-box';
import makeStyles from '@mui/styles/makeStyles';
import { useMessage } from 'modules/intl';
import { SearchCriteria } from 'modules/notes/common/components/SearchFacets';
import { getScrollStyles } from 'modules/common/styles';
import { LabelValueType } from 'modules/notes/common/components/SearchFacets/LabelFacetDetails';
import { useSupervisorInsightContext } from '../Notes/SupervisorInsightContextProvider';
import { useSearchFacetsInsights } from './hooks/useSearchFacetsInsights';

const useStyles = makeStyles((theme) => ({
  formControl: {
    display: 'flex',
    width: '100%'
  },
  container: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    marginLeft: theme.spacing(2)
  },
  offset: {
    marginTop: theme.spacing(2.5)
  }
}));

const useSearchBoxStyles = makeStyles((theme) => ({
  searchFacets: {
    overflow: 'auto',
    maxHeight: '41vh',
    ...getScrollStyles(theme)
  }
}));

type InsightSearchBoxProps = {
  className?: string;
};

const isSearchCriteriaEmpty = (criteria: SearchCriteria) => {
  const searchCriteria = criteria.criterions;

  return !Object.keys(searchCriteria).some(
    (i: string) => searchCriteria[i].length > 0
  );
};

const nonEmptyCriteria = (criteria: SearchCriteria) => {
  const searchCriteria = criteria.criterions;

  return (Object.values(searchCriteria).find((i) => i.length > 0) ||
    []) as LabelValueType[];
};

export const InsightSearchBox: React.FC<InsightSearchBoxProps> = ({
  className
}: InsightSearchBoxProps) => {
  const {
    searchCriteriaInsights,
    setSearchCriteriaInsights,
    setSearchLabelType
  } = useSupervisorInsightContext();

  const [selectedValue, setSelectedValue] = useState<LabelValueType[]>(
    nonEmptyCriteria(searchCriteriaInsights)
  );
  const [previousSearchCriteria, setPreviousSearchCriteria] = useState(
    searchCriteriaInsights
  );

  useEffect(() => {
    if (isSearchCriteriaEmpty(searchCriteriaInsights) === false) {
      setPreviousSearchCriteria(searchCriteriaInsights);
    }
  }, [searchCriteriaInsights]);

  useEffect(() => {
    if (
      isSearchCriteriaEmpty(searchCriteriaInsights) === true &&
      isSearchCriteriaEmpty(previousSearchCriteria) === false
    ) {
      setSearchLabelType('');
      setSelectedValue([]);
    }
  }, [
    previousSearchCriteria,
    searchCriteriaInsights,
    setSearchLabelType,
    setSelectedValue
  ]);

  const facets = useSearchFacetsInsights(selectedValue, setSelectedValue);
  const searchLabel = useMessage('search.title');

  const setSearchCriteria = useCallback(
    (searchCriteria: SearchCriteria) => {
      setSearchCriteriaInsights(searchCriteria);
    },
    [setSearchCriteriaInsights]
  );
  const classes = useStyles();
  const searchBoxClasses = useSearchBoxStyles();
  return (
    <div className={classNames(classes.container, className)}>
      <SearchBox
        classes={searchBoxClasses}
        searchHintLabel={searchLabel}
        facets={facets}
        searchCriteria={searchCriteriaInsights}
        setSearchCriteria={setSearchCriteria}
        hideSearch={true}
      />
    </div>
  );
};
export default InsightSearchBox;
